import type { Time } from "@suse-o11y/date-time/time";
import { min } from "@suse-o11y/min";
import type { NonEmptyArray } from "@suse-o11y/non-empty-array";
import { pluralize } from "@suse-o11y/pluralize";
import { prop } from "@suse-o11y/prop";
import { unique } from "@suse-o11y/unique";
import React from "react";

import { ViewTimeLink } from "./react/viewtimelink/ViewTimeLink";

const pluralizeSilencingRule = pluralize({
	1: "1\u00A0silencing rule",
	more: (count) => `${count}\u00A0silencing rules`,
});

const pluralizeSilencingNote = pluralize({
	1: "note",
	more: "notes",
});

interface Reason {
	startTimeEpochMs: number;
	note: string;
}

interface MutedSinceMessageTimestampOnlyProps {
	timestamp: Time;
	reasons?: never;
}

interface MutedSinceMessageWithReasonsProps {
	timestamp?: never;
	reasons: NonEmptyArray<Reason>;
}

export const MutedSinceMessage = React.forwardRef<
	React.ComponentRef<"div">,
	(MutedSinceMessageTimestampOnlyProps | MutedSinceMessageWithReasonsProps) &
		Omit<React.ComponentPropsWithoutRef<"div">, "children">
>(({ timestamp, reasons, ...props }, ref) => {
	const [time, notes] = React.useMemo(() => {
		if (reasons == null) {
			return [timestamp, []];
		}

		const [firstReason, ...restReasons] = reasons;

		return [
			min(firstReason.startTimeEpochMs, restReasons.map(prop("startTimeEpochMs"))),
			unique(reasons.map(({ note }) => note.trim()).filter((note) => note.length > 0)),
		];
	}, [timestamp, reasons]);

	return (
		<div ref={ref} {...props}>
			Muted since: <ViewTimeLink hideTooltip time={time} />
			{reasons != null && reasons.length > 0 && <> via {pluralizeSilencingRule(reasons.length)}</>}
			{notes.length > 0 && (
				<>
					{" "}
					with the following {pluralizeSilencingNote(notes.length)}: <i className="italic">"{notes[0]}"</i>
					{notes.length > 1 && <> + {notes.length - 1} other</>}
				</>
			)}
		</div>
	);
});
